import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, Grid, IconButton, TextField, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import CustomBackdrop from '../../../../components/CustomBackdrop/CustomBackdrop';
import CustomFooter from '../../../../components/Footer/CustomFooter';
import {
  useAddCoreKeywordMutation,
  useGetCompanyCoreKeywordsQuery,
  useRejectCoreKeywordMutation,
} from '../../../../gql/generated/graphql';

import { validateKeywordsStyles } from './styles';

type Props = {
  companyId: number;
};

const ValidateKeywords = ({ companyId }: Props) => {
  const { t } = useTranslation();
  const [additionalKeyword, setAdditionalKeyword] = useState('');
  const [selectedKeywords, setSelectedKeywords] = useState<number[]>([]);

  const {
    data,
    loading: keywordsLoading,
    refetch: refetchKeywords,
  } = useGetCompanyCoreKeywordsQuery({ variables: { companyId } });
  const coreKeywords = data?.getCompanyCoreKeywords?.edges?.map(edge => edge?.node);

  const [rejectCoreKeyword, { loading: rejectLoading }] = useRejectCoreKeywordMutation();
  const [addCoreKeyword, { loading: addLoading }] = useAddCoreKeywordMutation();

  const columns = [
    {
      field: 'keyword',
      width: 200,
      headerName: t('validation.validateKeyword.keywordColumnLabel'),
    },
    {
      field: 'source',
      headerName: t('validation.validateKeyword.searchEngineColumnLabel'),
    },
    {
      field: 'cpc',
      headerName: t('validation.validateKeyword.cpcColumnLabel'),
      valueFormatter: (value: number) => {
        if (value === -1) return 'n.a.';
        if (value === -2) return 'n/a';
        if (value == null) return '';
        return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
      },
    },
    {
      field: 'searchVolume',
      headerName: t('validation.validateKeyword.sevoColumnLabel'),
    },
    {
      field: 'actions',
      headerName: '',
      editable: false,
      sortable: false,
      renderCell: params => (
        <>
          <IconButton size="small" onClick={() => handleDelete(params.row)}>
            <Tooltip title={t('validation.validateKeyword.deleteButtonTooltip')}>
              <DeleteOutlineIcon fontSize="small" />
            </Tooltip>
          </IconButton>
        </>
      ),
    },
  ];

  const handleDelete = async row => {
    const result = await rejectCoreKeyword({
      variables: { keywordId: row.dbId },
    });
    if (result.data?.rejectCoreKeyword?.success) {
      await refetchKeywords();
    }
  };

  const handleDeleteSelected = async (ids: number[]) => {
    for (const id of ids) {
      await rejectCoreKeyword({ variables: { keywordId: id } });
    }
    await refetchKeywords();
    setSelectedKeywords([]);
  };

  const handleAddKeywords = async () => {
    const keywords = additionalKeyword
      .split(',')
      .map(k => k.trim())
      .filter(k => k);
    if (keywords.length === 0) return;

    for (const kw of keywords) {
      await addCoreKeyword({ variables: { companyId, keyword: kw } });
    }
    await refetchKeywords();
    setAdditionalKeyword('');
  };

  return (
    <Box display="flex" flex={1} flexDirection="column">
      <DataGrid
        sx={validateKeywordsStyles.dataGridMargin}
        disableRowSelectionOnClick
        rows={coreKeywords || []}
        loading={keywordsLoading}
        columns={columns}
        rowHeight={35}
        initialState={{
          sorting: {
            sortModel: [{ field: 'cpc', sort: 'desc' }],
          },
          pagination: {
            paginationModel: {
              pageSize: 80,
            },
          },
        }}
        pageSizeOptions={[10, 25, 50, 80]}
        checkboxSelection
        getRowId={row => row?.dbId}
        rowSelectionModel={selectedKeywords}
        onRowSelectionModelChange={(ids: number[]) => setSelectedKeywords(ids)}
        slots={{
          footer: () => (
            <CustomFooter
              onRefresh={() => refetchKeywords()}
              onDeleteSelected={() => handleDeleteSelected(selectedKeywords)}
            />
          ),
        }}
      />
      <Grid container spacing={2}>
        <Grid item md={8} xs={12}>
          <TextField
            label={t('validation.validateKeyword.additionalKeywordsTextfieldLabel')}
            id="additional_keywords"
            fullWidth
            value={additionalKeyword}
            onChange={e => setAdditionalKeyword(e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item md={4} xs={6}>
          <Button onClick={handleAddKeywords} variant="contained">
            {t('validation.validateKeyword.addButton')}
          </Button>
        </Grid>
      </Grid>
      <CustomBackdrop open={rejectLoading || addLoading} />
    </Box>
  );
};

export default ValidateKeywords;
